import React, { useState, useEffect, useRef } from 'react';
import {
    Image as ImageIcon,
    Upload,
    Trash2,
    Loader2,
    AlertCircle,
    CheckCircle2,
    Crown,
} from 'lucide-react';
import {
    getBrandingApi,
    uploadBrandingLogoApi,
    deleteBrandingLogoApi,
} from '../services/brandingApi';

const ACCEPTED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB — matches the backend cap

/**
 * Custom branding (logo) card for the Organisation settings page.
 *
 * The logo is used on invoice PDFs and customer emails. Uploading works on any
 * plan (soft gate), but the logo is only *rendered* while the plan has the
 * custom-branding feature — surfaced here via the `customBrandingActive` flag.
 */
const BrandingCard = () => {
    const [branding, setBranding] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isBusy, setIsBusy] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);

    const load = async () => {
        setError(null);
        try {
            setBranding(await getBrandingApi());
        } catch (err) {
            if (err.response?.status === 403) {
                setError('You do not have permission to manage branding.');
            } else {
                setError(err.response?.data?.message || 'Failed to load branding.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePick = () => fileInputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;

        if (!ACCEPTED.includes(file.type)) {
            setError('Unsupported file type. Use JPEG, PNG, GIF or WebP.');
            return;
        }
        if (file.size > MAX_BYTES) {
            setError('Logo is too large. Maximum size is 5 MB.');
            return;
        }

        setIsBusy(true);
        setError(null);
        try {
            const result = await uploadBrandingLogoApi(file);
            setBranding(result);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload logo.');
        } finally {
            setIsBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remove your custom logo? It will no longer appear on invoices or emails.')) {
            return;
        }
        setIsBusy(true);
        setError(null);
        try {
            await deleteBrandingLogoApi();
            setBranding(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove logo.');
        } finally {
            setIsBusy(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-2 text-gray-400">
                    <Loader2 size={16} className="animate-spin" />
                    <span className="text-sm">Loading branding…</span>
                </div>
            </div>
        );
    }

    const hasLogo = !!branding;
    const active = !!branding?.customBrandingActive;

    return (
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 bg-violet-50 rounded-lg flex items-center justify-center">
                        <ImageIcon size={16} className="text-violet-600" />
                    </div>
                    <h3 className="font-semibold text-gray-900">Custom branding</h3>
                </div>
                {hasLogo && (
                    active ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-green-100 text-green-800">
                            <CheckCircle2 size={12} /> Active
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                            Not on your plan
                        </span>
                    )
                )}
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            {/* Upgrade hint when a logo exists but the plan doesn't render it */}
            {hasLogo && !active && (
                <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg flex items-start gap-2 text-sm text-violet-800">
                    <Crown size={16} className="flex-shrink-0 mt-0.5" />
                    <span>Your logo is saved but only appears on invoices and emails on the Enterprise plan.</span>
                </div>
            )}

            {hasLogo ? (
                <>
                    <div className="mb-4 flex items-center justify-center bg-gray-50 border border-gray-200 rounded-lg p-4">
                        {branding.url ? (
                            <img
                                src={branding.url}
                                alt={branding.originalFilename || 'Logo'}
                                className="max-h-24 max-w-full object-contain"
                            />
                        ) : (
                            <span className="text-sm text-gray-400">{branding.originalFilename}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handlePick}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50"
                        >
                            {isBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                            Replace
                        </button>
                        <button
                            onClick={handleDelete}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
                        >
                            <Trash2 size={14} /> Remove
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <p className="text-sm text-gray-600 mb-4">
                        Upload your logo to display it on invoice PDFs and customer emails.
                        Shown on the Enterprise plan. PNG, JPEG, GIF or WebP, up to 5&nbsp;MB.
                    </p>
                    <button
                        onClick={handlePick}
                        disabled={isBusy}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 transition-colors disabled:opacity-50"
                    >
                        {isBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                        Upload logo
                    </button>
                </>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED.join(',')}
                onChange={handleFile}
                className="hidden"
            />
        </div>
    );
};

export default BrandingCard;
